/**
 * Image Generation Service
 */
import type { Jai1Config } from '../types/framework.types.js';
import { NetworkError } from '../errors/index.js';

// Supported image sizes
export type ImageSize = '1024x1024' | '1792x1024' | '1024x1792' | '1024x768' | '768x1024';

// Supported quality options
export type ImageQuality = 'standard' | 'hd';

export interface GenerateImageOptions {
    prompt: string;
    model: string;
    size?: ImageSize;
    quality?: ImageQuality;
}

export interface ImageResult {
    id: number;
    url: string;
    prompt: string;
    model: string;
    size?: string;
    quality?: string;
    contentSize: number;
    createdAt: string;
}

export interface ListImagesOptions {
    model?: string;
    limit?: number;
    offset?: number;
}

export interface ListImagesResult {
    images: Array<{
        id: number;
        url: string;
        prompt: string;
        model: string;
        createdAt: string;
    }>;
    total: number;
    limit: number;
    offset: number;
}

export class ImageService {
    /**
     * Generate image from prompt
     */
    async generate(config: Jai1Config, options: GenerateImageOptions): Promise<ImageResult> {
        const response = await fetch(`${config.apiUrl}/api/image/generate`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'JAI1-Access-Key': config.accessKey,
            },
            body: JSON.stringify({
                prompt: options.prompt,
                model: options.model,
                size: options.size || '1024x1024',
                quality: options.quality || 'hd',
            }),
        });

        if (!response.ok) {
            let errorMessage = `HTTP ${response.status}`;
            try {
                const error = await response.json();
                errorMessage = error.message || errorMessage;
            } catch (e) {
                // Ignore parse error
            }
            throw new NetworkError(errorMessage);
        }

        const data = await response.json();
        return data.data;
    }

    /**
     * List generated images
     */
    async list(config: Jai1Config, options: ListImagesOptions): Promise<ListImagesResult> {
        const params = new URLSearchParams();
        if (options.model) params.set('model', options.model);
        if (options.limit) params.set('limit', options.limit.toString());
        if (options.offset) params.set('offset', options.offset.toString());

        const url = `${config.apiUrl}/api/image/list?${params}`;

        const response = await fetch(url, {
            headers: {
                'JAI1-Access-Key': config.accessKey,
            },
        });

        if (!response.ok) {
            throw new NetworkError(`Failed to list images: HTTP ${response.status}`);
        }

        const data = await response.json();
        return data.data;
    }

    /**
     * Get image info
     */
    async getInfo(config: Jai1Config, id: number): Promise<any> {
        const response = await fetch(`${config.apiUrl}/api/image/${id}`, {
            headers: {
                'JAI1-Access-Key': config.accessKey,
            },
        });

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error('Image not found');
            }
            throw new NetworkError(`Failed to get image: HTTP ${response.status}`);
        }

        const data = await response.json();
        return data.data;
    }

    /**
     * Delete image
     */
    async delete(config: Jai1Config, id: number): Promise<void> {
        const response = await fetch(`${config.apiUrl}/api/image/${id}`, {
            method: 'DELETE',
            headers: {
                'JAI1-Access-Key': config.accessKey,
            },
        });

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error('Image not found');
            }
            throw new NetworkError(`Failed to delete image: HTTP ${response.status}`);
        }
    }
}
